import { useState } from 'react';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { plural } from '../../../../../shared/helpers';
import { EnumReachableAssumed, EnumReachableConfirmed } from '../../../../../shared/reachability/types';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import { DocsLink, Icon, icons, Select } from '../../../generic';
import { isProjectReachable } from '../../utils';
import type { Project, VulnIssue } from '../../../../../shared/issuesV2';

import Visualization from './Visualization';

import styles from './Reachability.module.less';

function getProjectSelectLabel(projects: Project[]): string {
  return `Reachable in ${projects.length} ${plural(projects.length, 'project')}`;
}

/**
 * Displays information related to the reachability of this vuln. For each
 * project in which this vuln is reachable, present the user with a visualization
 * of the call paths leading to where the vuln is reached.
 *
 * In Project scope, only show reachability info for the project at hand and hide
 * the dropdown that allows selecting other projects.
 */
const IssueDetailsReachability: VoidFunctionComponent = () => {
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const issue = useSelector(IssuesV2Store.selectors.getIssueDetails) as VulnIssue | null;
  const projects = (issue?.projects ?? []).filter(isProjectReachable);

  const isProjectScope = scope.type === 'project';

  // choose the initial project based on the current scope
  const initialProject = isProjectScope
    ? projects.find((project) => project.id === scope.id)
    : projects[0];
  const [selectedProjectId, setSelectedProjectId] = useState(initialProject?.id ?? '');
  const selectedProject = projects.find((project) => project.id === selectedProjectId);

  // handle header description based on reachability state
  const getHeaderDescription = () => {
    if (isProjectScope) {
      if (selectedProject?.reachability?.reachable === EnumReachableConfirmed) {
        return 'The vulnerability is reachable via the functions identified below.';
      }
      if (selectedProject?.reachability?.reachable === EnumReachableAssumed) {
        return 'Did not detect any reachable functions for the vulnerability. However, be cautious of the identified vulnerable functions listed below.';
      }
    }
    return 'This vulnerability may affect more than one project. Use the dropdown to see the reachability analysis for each project.';
  };

  return (
    <div className={styles.reachability}>
      <div className={styles.header}>
        <div className={styles.headerName}>
          {selectedProject?.reachability?.reachable === EnumReachableConfirmed ? 'Reachable functions' : 'Vulnerable function data available'}
        </div>
        {getHeaderDescription()}
      </div>

      {/* Only show the project selection dropdown in Global / RG scopes */}
      {!isProjectScope && (
        <div className={styles.project}>
          <div className={styles.projectHeader}>{getProjectSelectLabel(projects)}</div>
          <Select
            id="reachability-project-select"
            placeholder="Select a project"
            value={{ value: selectedProjectId, label: selectedProjectId }}
            options={projects.map((project) => ({ value: project.id, label: project.title }))}
            onChange={(option) => {
              if (option) {
                setSelectedProjectId(option.value);
              }
            }}
            isDisabled={projects.length <= 1}
          />
        </div>
      )}

      <Visualization
        reachable={selectedProject?.reachability?.reachable}
        callPaths={selectedProject?.reachability?.callPaths}
      />

      <div className={styles.notice}>
        <div className={styles.noticeBody}>
          Reachability analysis is not perfect. The identified functions are statically discovered.
          There may be additional dynamic instances of vulnerable functions that FOSSA can&apos;t find
          (eg. after the project is built). The context of how this function is used may also be
          important to determining if this finding is valid as reachable.
        </div>
        <div>
          More at&nbsp;
          <DocsLink to="/reachability">
            Docs/Reachability
            <span className={styles.noticeLinkIcon}>
              <Icon icon={icons.externalLink} />
            </span>
          </DocsLink>
        </div>
      </div>
    </div>
  );
};

export default IssueDetailsReachability;
